import { addDays, subDays, startOfDay, endOfDay, format, startOfToday } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { gql } from "@apollo/client";
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import UnassignedVisitsView from "~/views/patients/UnassignedVisitsView";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { useEffect, useMemo, useState } from "react";

import { datef } from "@caredna-marlin/utils/dates";
import PermissionOrWarn from "~/components/common/PermissionOrWarn";
import { Permission } from "~/hooks/permissions";

import { useVisitWorklistWarningsQuery, VisitWorklistWarningsQuery } from "~/graphql/__generated__/graphql";

import VisitIssuesIcon from "./VisitIssuesIcon";
import VisitPopup from "~/components/command/VisitPopup";
import CreateOverrideVisit from "~/components/scheduler/CreateOverrideVisit";

import WarningFilterChips, {
  WarningFilterValues,
  Options as FilterOptions,
  DEFAULT_WARNING_CATEGORIES,
} from "./WarningFilterChips";
import WarningFilterDialog from "./WarningFilterDialog";
import { WARNING_CATEGORY_LABELS, WarningCategory, WarningSeverityValue } from "@caredna-marlin/utils/warnings";
import CancelVisitDialog from "~/components/command/CancelVisitDialog";
import RefreshButton from "~/components/common/RefreshButton";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { last, sortBy } from "lodash";
import { CalendarPopup } from "~/components/common/calendar/CalendarPopup";
import { UNASSIGNED_PROVIDER_ID } from "~/utils/visitsHelper";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import useDefaultServiceLines from "~/hooks/useDefaultServiceLines";
import { HomeHealthComplianceTab, useHomeHealthDemoEnabled } from "~/features/home-health-demo";
import { useHandsOnOrg } from "~/features/hcah-hands-on";

/* ---------------- MAIN ---------------- */

const VisitAlerts = () => {
  const [searchParams] = useSearchParams();
  const [selectedTab, setTab] = useState<'issues' | 'unassigned' | 'compliance'>(
    searchParams.get('tab') === 'unassigned' ? 'unassigned' : searchParams.get('tab') === 'compliance' ? 'compliance' : 'issues'
  );
  const homeHealthDemo = useHomeHealthDemoEnabled();
  // The hands-on org has no Unassigned Visits tab; a link straight to it lands on Visit Issues.
  const showUnassigned = !useHandsOnOrg();
  const tab = selectedTab === 'unassigned' && !showUnassigned ? 'issues' : selectedTab;

  // 30-day window anchored to start-of-day
  const [startDate, setStartDate] = useState<Date>(startOfToday);

  const variables = useMemo(() => {
    const endDate = endOfDay(addDays(startDate, 30))
    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
    }
  }, [startDate]);

  // refresh timing
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const { data, loading, refetch, networkStatus } = useVisitWorklistWarningsQuery({
    variables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: () => setLastRefreshedAt(new Date()),
  });

  const rows = useMemo(() => data?.visitWorklistWarnings ?? [], [data]);

  const handleRefresh = async () => {
    await refetch();
    setLastRefreshedAt(new Date());
  };

  // selected visit for the details pane
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
  const [visitIdToBeEdited, setVisitIdToBeEdited] = useState<null | number>(null);
  const [visitIdToBeCanceled, setVisitIdToBeCanceled] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<null | HTMLElement>(null);

  const selectedVisit = useMemo(
    () => rows.find(r => r.visit?.id === selectedVisitId) ?? null,
    [rows, selectedVisitId]
  );

  // Month navigation
  const goPrevWindow = () => setStartDate(d => startOfDay(subDays(d, 30)));
  const goNextWindow = () => setStartDate(d => startOfDay(addDays(d, 30)));

  const serviceLineIds = useDefaultServiceLines().map(slid => slid.toString());

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<WarningFilterValues>({
    visitTypeIds: [],
    patientIds: [],
    providerIds: [],
    serviceLineIds,
    warningCategories: DEFAULT_WARNING_CATEGORIES,
  });

  const visitTypeOptions: FilterOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      const vt = r.visit.visitType;
      map.set(vt.id.toString(), vt.name);
    }
    return map
  }, [rows]);

  const patientOptions: FilterOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      const p = r.visit.patient;
      map.set(p.id.toString(), `${p.firstName} ${p.lastName}`);
    }
    return map
  }, [rows]);

  const providerOptions: FilterOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      for (const vr of r.visitResources ?? []) {
        const prov = vr.visitResource.provider;
        if (!prov) {
          map.set(UNASSIGNED_PROVIDER_ID.toString(), "Unassigned");
          continue;
        }

        const label = `${prov.firstName} ${prov.lastName}`.trim();
        map.set(prov.id.toString(), label);
      }
    }
    return map
  }, [rows]);

  const { serviceLines } = useOrgConfig()
  const serviceLineOptions: FilterOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const sl in serviceLines) {
      map.set(sl, serviceLines[sl]!.name);
    }
    return map
  }, [serviceLines]);

  // If the options change, remove any filters that are no longer in options
  useEffect(() => {
    const inOptions = (filterArr: string[], options: FilterOptions): string[] => {
      return filterArr.filter(f => options.get(f))
    }
    setFilters({
      visitTypeIds: inOptions(filters.visitTypeIds, visitTypeOptions),
      patientIds: inOptions(filters.patientIds, patientOptions),
      providerIds: inOptions(filters.providerIds, providerOptions),
      serviceLineIds: inOptions(filters.serviceLineIds, serviceLineOptions),
      warningCategories: filters.warningCategories,
    })
  }, [visitTypeOptions, patientOptions, providerOptions])

  // Apply filters BEFORE pagination, trimming warning lists by severity
  const filteredRows = useMemo(() => filterVisitWorklistRows(rows, filters), [rows, filters]);

  return (
    <PermissionOrWarn permission={Permission.useVisitWorklist}>
      <Box sx={{ height: "100%", width: "100%", overflow: "hidden", mt: 4, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Paper sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                <Tab label="Visit Issues" value="issues" />
                {showUnassigned && <Tab label="Unassigned Visits" value="unassigned" />}
                {homeHealthDemo && <Tab label="Compliance" value="compliance" />}
              </Tabs>

              {tab === 'compliance' && homeHealthDemo && (
                <Box sx={{ p: 2 }}>
                  <HomeHealthComplianceTab />
                </Box>
              )}

              {tab === 'unassigned' && (
                <Box sx={{ p: 2 }}>
                  <UnassignedVisitsView />
                </Box>
              )}

              {tab === 'issues' && <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton size="small" onClick={goPrevWindow} aria-label="Previous 30 days">
                      <ArrowBackIosNewIcon fontSize="inherit" />
                    </IconButton>

                    <Typography variant="body2" color="text.secondary">
                      {format(variables.start, "MMM d, yyyy")}
                      {" - "}
                      {format(variables.end, "MMM d, yyyy")}
                    </Typography>

                    <IconButton size="small" onClick={goNextWindow} aria-label="Next 30 days">
                      <ArrowForwardIosIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>
                </Stack>

                <RefreshButton
                  onRefresh={handleRefresh}
                  lastUpdatedAt={lastRefreshedAt}
                  disabled={networkStatus === 4 || loading}
                />
              </Stack>

              <WarningFilterChips
                values={filters}
                setValues={setFilters}
                onOpenDialog={() => setFilterDialogOpen(true)}
                visitTypeOptions={visitTypeOptions}
                patientOptions={patientOptions}
                providerOptions={providerOptions}
                serviceLineOptions={serviceLineOptions}
              />

              <VisitTable
                rows={filteredRows}
                loading={loading || networkStatus === 4}
                selectedVisitId={selectedVisitId}
                setSelectedVisitId={setSelectedVisitId}
                setSelectedRow={setSelectedRow}
              />

              {filterDialogOpen && <WarningFilterDialog
                onClose={() => setFilterDialogOpen(false)}
                onSave={setFilters}
                values={filters}
                visitTypeOptions={visitTypeOptions}
                patientOptions={patientOptions}
                providerOptions={providerOptions}
              />}
              </Box>} {/* end tab === 'issues' */}
            </Paper>
          </Grid>
        </Grid>

        {selectedVisit && (
          <CalendarPopup
            isOpen={true}
            visitAnchor={selectedRow}
            onClose={() => setSelectedVisitId(null)}
          >
            <VisitPopup
              visit={selectedVisit.visit}
              visitResourceId={highestSeverityResource(selectedVisit.visitResources)?.visitResource.id}
              onClose={() => setSelectedVisitId(null)}
              onCancel={(visitId) => {
                setSelectedVisitId(null);
                setVisitIdToBeCanceled(visitId);
              }}
              onEdit={setVisitIdToBeEdited}
            />
          </CalendarPopup>
        )}

        {visitIdToBeEdited && (
          <CreateOverrideVisit
            key={visitIdToBeEdited}
            handleClose={() => setVisitIdToBeEdited(null)}
            calendarDateTime={new Date()}
            onSuccess={() => setVisitIdToBeEdited(null)}
            visitId={visitIdToBeEdited}
          />
        )}

        {visitIdToBeCanceled && <CancelVisitDialog
          visitId={visitIdToBeCanceled}
          onClose={() => setVisitIdToBeCanceled(null)}
          onSuccess={() => {
            setVisitIdToBeCanceled(null);
            setSelectedVisitId(null);
            handleRefresh();
          }}
        />}
      </Box>
    </PermissionOrWarn>
  );
};

export default VisitAlerts;

/* ---------------- TABLE ---------------- */

type Row = NonNullable<VisitWorklistWarningsQuery["visitWorklistWarnings"]>[number];

type TableProps = {
  rows: Row[];
  loading: boolean;
  selectedVisitId: number | null;
  setSelectedVisitId: (id: number) => void;
  setSelectedRow: (target: HTMLElement) => void;
};

const cols: string[] = ["Issues", "Start Time", "Visit Type", "Patient", "Providers"];

const VisitTable = ({ rows, loading, selectedVisitId, setSelectedVisitId, setSelectedRow }: TableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Reset pagination if number of rows change due to filters or date range.
  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const visibleRows = useMemo(
    () => rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const { timeLabelWithZone } = useZonedDateFormat();

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {cols.map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <RowSkeleton />
            ) : visibleRows.length === 0 ? (
              <NoIssues />
            ) : (
              visibleRows.map((r, idx) => {
                const v = r.visit!;
                const isSyntheticRow = v.id <= 0;
                const patient = v.patient!;
                const providers = (r.visitResources ?? [])
                  .map((vr) => `${vr.visitResource.provider?.firstName ?? ""} ${vr.visitResource.provider?.lastName ?? ""}`.trim())
                  .filter(Boolean)
                  .join(", ");

                const visitWarnings = r.visitWarnings.map((vw) => ({...vw, severity: vw.severity as WarningSeverityValue}));
                const perResource = (r.visitResources ?? []).map((vr) => {
                  const providerName = `${vr.visitResource.provider?.firstName ?? ""} ${vr.visitResource.provider?.lastName ?? ""}`.trim();
                  return {
                    providerName: providerName || "Provider",
                    warnings: vr.warnings.map((warning) => ({...warning, severity: warning.severity as WarningSeverityValue})),
                  };
                });

                return (
                  <TableRow
                    key={`${v.id}-${idx}`}
                    hover
                    selected={!isSyntheticRow && selectedVisitId === v.id}
                    sx={{ cursor: isSyntheticRow ? "default" : "pointer" }}
                    onClick={(e) => {
                      if (isSyntheticRow) return;
                      setSelectedVisitId(v.id)
                      setSelectedRow(e.currentTarget)
                    }}
                  >
                    <TableCell>
                      <VisitIssuesIcon visitWarnings={visitWarnings} perResource={perResource} />
                    </TableCell>
                    <TableCell>{datef(new Date(v.startTime), "MMM d")}, {timeLabelWithZone(v.startTime, true, v.addressTimezone)}</TableCell>
                    <TableCell>{v.visitType?.name}</TableCell>
                    <TableCell>
                      {patient.firstName} {patient.lastName}
                    </TableCell>
                    <TableCell>{providers}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(parseInt(e.target.value));
        }}
      />
    </>
  );
};

const RowSkeleton = () => (
  <TableRow>
    <TableCell><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
  </TableRow>
);

// The service returns only visits with issues, so an empty table says nothing about whether there are visits.
const NoIssues = () => (
  <TableRow>
    <TableCell colSpan={5} sx={{ textAlign: "center" }}>
      No issues matching filters found within the selected date range
    </TableCell>
  </TableRow>
);

const filterWarningsByCategory = <T extends { severity: number, category: string }>(
  warnings: T[] | undefined,
  categories: WarningCategory[],
): T[] => {
  const safeWarnings = warnings ?? [];
  if (categories.length == 0) return safeWarnings;
  return safeWarnings.filter(w => categories.map(c => WARNING_CATEGORY_LABELS[c]).includes(w.category));
}

function filterVisitWorklistRows(
  rows: Row[],
  filters: WarningFilterValues
): Row[] {
  const vtSet = new Set(filters.visitTypeIds);
  const pSet = new Set(filters.patientIds);
  const provSet = new Set(filters.providerIds);
  const serviceLineSet = new Set(filters.serviceLineIds);
  const categories = filters.warningCategories;

  return rows
  // Row eligibility (visitType/patient/provider/serviceLine)
  .filter((r) => {
    const v = r.visit!;
    const vtOk = vtSet.size === 0 || (v.visitType?.id && vtSet.has(String(v.visitType.id)));

    const patientId = (v.patient as any)?.id as number | undefined;
    const pOk = pSet.size === 0 || (patientId && pSet.has(String(patientId)));

    const providersInRow = (r.visitResources ?? [])
      .map((vr) => vr.visitResource.provider?.id)
      .filter(Boolean)
      .map(String);
    const provOk = provSet.size === 0 || providersInRow.some((id) => provSet.has(id));

    const slOk = serviceLineSet.size === 0 || serviceLineSet.has(r.visit.episode.serviceLineId.toString())

    return vtOk && pOk && provOk && slOk;
  })
  // Transform: filter warnings by severity, and drop any resource that has zero warnings after severity filter.
  .map((r) => {
    const filteredVisitWarnings = filterWarningsByCategory(r.visitWarnings, categories);

    const filteredVisitResources = (r.visitResources ?? [])
      .map((vr) => {
        const filteredWarnings = filterWarningsByCategory(vr.warnings, categories);
        return { ...vr, warnings: filteredWarnings };
      })

    return {
      ...r,
      visitWarnings: filteredVisitWarnings,
      visitResources: filteredVisitResources,
    };
  })
  // Drop row if there are no warnings
  .filter((r) => r.visitWarnings.length > 0 || r.visitResources.flatMap((vr) => vr.warnings).length > 0);
}

const highestSeverityResource = (visitResources: Row["visitResources"]): Row["visitResources"][number] | undefined => {
  return last(sortBy(visitResources, (vr) => Math.max(...vr.warnings.map(w => w.severity))));
}

/* ---------------- GRAPHQL ---------------- */

gql(`
  query VisitWorklistWarnings($start: String!, $end: String!)
  @permission(requires: useVisitWorklist) {
    visitWorklistWarnings(start: $start, end: $end) {
      visit {
        id
        startTime
        endTime
        visitType { id name }
        patient { id firstName lastName }
        addressTimezone
        episode {
          id
          serviceLineId
        }
      }
      visitWarnings {
        severity
        category
        warning
      }
      visitResources {
        visitResource {
          id
          provider { id firstName lastName }
        }
        warnings {
          severity
          category
          warning
        }
      }
    }
  }
`);
